/**
 * What a signed-in caller who is not an operator sees at `/` -- refused
 * or unreachable alike (see `OpsShell`'s doc comment). The bundle is served to
 * whoever requests `ops.html`, so this sentence and one control -- sign out and
 * land on the application's own sign-in -- is the whole of what an accidental
 * visitor gets: no nav, no tenant name, no count of anything.
 */

import type { ReactNode } from 'react';
import { Activity, ArrowLeft } from 'lucide-react';

import { useSignOut } from '../../app/session/authState';
import { APP_ORIGIN } from '../../lib/origins';
import { defineCatalogue } from '../../i18n';
import { Button } from '@/ui/button';
import { Card, CardContent } from '@/ui/card';
import { EmptyMedia } from '@/ui/empty';

// Not imported from `src/app/routes/paths`: `src/ops` may not take a
// structural dependency on the tenant application's route composition
// (`src/ops/routes/__tests__/composition.test.ts`), and this is a
// cross-origin redirect, not a route inside either plane's router.
const SIGN_IN_PATH = '/sign-in';

const catalogue = defineCatalogue(
  {
    title: 'Esta não é a sua aplicação',
    body: 'A operação da SocialSrc atende as organizações por dentro. Sua conta acompanha campanhas, e é para lá que ela entra.',
    link: 'Ir para minhas organizações',
  },
  {
    title: 'This is not your application',
    body: "SocialSrc's own operation serves organisations from the inside. Your account tracks campaigns, and that is where it belongs.",
    link: 'Go to my organisations',
  },
);

export function NotAnOperator(): ReactNode {
  const signOut = useSignOut();

  // Firebase keeps this origin's session apart from the tenant site's, so
  // signing out anywhere else never reaches it, and the redirect runs whether
  // or not the sign-out call itself succeeds -- the point is leaving this
  // screen, not the round trip.
  const goToLogin = (): void => {
    void signOut().finally(() => {
      window.location.href = `${APP_ORIGIN}${SIGN_IN_PATH}`;
    });
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-muted/30 p-8">
      <Card
        data-testid="not-an-operator"
        role="alert"
        className="w-full max-w-sm items-center text-center"
      >
        <CardContent className="flex flex-col items-center gap-4">
          <EmptyMedia variant="icon" className="bg-primary/15 text-primary mb-0">
            <Activity aria-hidden />
          </EmptyMedia>
          <h1 className="text-title-md">{catalogue.t('title')}</h1>
          <p className="text-muted-foreground text-body-sm">{catalogue.t('body')}</p>
          <Button type="button" className="w-full" onClick={goToLogin}>
            <ArrowLeft aria-hidden />
            {catalogue.t('link')}
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}
